/*
 * notify-stock.js — back-in-stock email subscription for out-of-stock sizes
 * (.claude/.specs/back-in-stock-notification.md).
 *
 * Triggers (.notify-stock) come in two scopes:
 * - variant-scoped bells (snippets/notify-stock-button.liquid, data-variant-id
 *   set): above OOS pills in the variant picker and in place of the quantity
 *   selector in OOS rows of the footwear/fashion size table;
 * - product-scoped CTA on the product card (snippets/card-product.liquid,
 *   .notify-stock--cta, NO data-variant-id): replaces "Alege Marimea" when the
 *   whole product is 'Stoc Epuizat'. Klaviyo only takes per-variant
 *   subscriptions, so the CTA carries the product's sizes as a JSON
 *   data-notify-variants attribute ([{id, title}, …] — every variant, all OOS
 *   in that state) and ALWAYS opens the modal, logged-in included: the
 *   shopper multi-selects sizes there and one request is POSTed per selected
 *   size.
 *
 * On mobile (<= 749px) the 14px bell is too small to tap, so in the variant
 * picker the whole .variant-option-cell (bell + OOS size pill) is the tap
 * target. That is pure CSS (component-notify-stock.css): the bell's ::after
 * overlays the cell above the label, so a tap anywhere in an OOS cell is a
 * real click on the bell button — this handler needs no special casing —
 * and the label underneath never fires, so the OOS radio is NOT selected
 * (a variant re-render would replace the picker markup and wipe the
 * page-lifetime subscribed state).
 *
 * Logged-in bell clicks subscribe straight away with the account email
 * (data-customer-email, emitted by the Liquid — the size is already chosen);
 * every other click opens <notify-stock-modal>, with the email prefilled for
 * logged-in shoppers. On PDPs the modal is server-rendered for guests
 * (snippets/notify-stock-modal.liquid) with a data blob; anywhere else
 * (collections, search, sliders, logged-in card clicks) ensureModal() builds
 * the same shell from MODAL_SHELL and the card button supplies the product
 * info via data-notify-* attributes. openForVariant() always repaints every
 * modal field from its source, so one shell serves any product on the page.
 *
 * Subscriptions go straight to Klaviyo's client Back in Stock API (no theme
 * or dispofy-app backend): POST {ENDPOINT}?company_id={public key} with a
 * JSON:API body per variant — catalog-variant id format
 * '$shopify:::$default:::<variant id>'. Success is HTTP 202 with an empty
 * body. The public key comes from the theme setting klaviyo_public_api_key
 * (data-klaviyo-key on each trigger), with a fallback sniff of the Klaviyo
 * app embed's klaviyo.js script tag. The restock email itself is a Klaviyo
 * flow configured in their dashboard. Nothing here is optimistic: the
 * success toast + subscribed state require at least one accepted request —
 * a partial multi-size failure still shows success (the shopper WILL be
 * notified for the accepted sizes; the rest is console.warn'd). Only when
 * every request fails does the generic message surface — red
 * .notify-toast--error variant on logged-in bell clicks, the modal's inline
 * error otherwise — while the real Klaviyo detail (HTTP status + response
 * body, or the network error) goes to console.error. The subscribed state is page-lifetime only; a reload
 * re-arms the trigger (Klaviyo deduplicates repeat subscriptions).
 *
 * Loaded once per page via a window guard — the button/modal snippets emit the
 * tag per instance and re-executions bail out here.
 */
(function () {
	if (window.__notifyStock) return;
	window.__notifyStock = true;

	var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
	var REVISION = '2026-07-15';
	/* Gap between consecutive multi-size requests — see the SEQUENTIAL AND
	   SPACED comment in subscribe() before touching this. Empirically: 300ms
	   spacing registered 1/7, 1000ms registered 3/5, and even 2000ms sometimes
	   topped out at 3 accepted — Klaviyo drops same-profile subscriptions
	   ingested too close together even though every request gets a 202. Since
	   batches are capped at MAX_SIZES anyway, the spacing stays at 1s. */
	var REQUEST_SPACING_MS = 1000;
	/* Hard cap on sizes per submit — Klaviyo empirically never accepted more
	   than ~3 same-profile subscriptions per batch regardless of spacing.
	   Enforced in the chip click handler; mirrored by the "(maxim 3)" label in
	   MODAL_SHELL and snippets/notify-stock-modal.liquid — keep all in sync. */
	var MAX_SIZES = 3;
	var TOAST_TEXT = 'Vei fi notificat cand produsul revine in stoc!';
	var GENERIC_ERROR = 'A aparut o eroare. Incearca din nou.';
	/* Same bell as notify-stock-button.liquid (Figma node 793-1160). */
	var BELL_PATH =
		'M4.17677 11.424L1.73077 11.143C-0.124228 10.93 -0.649228 8.484 0.952772 7.527L1.01777 7.489C1.4467 7.23256 1.80181 6.86926 2.04841 6.4346C2.29501 5.99993 2.42469 5.50875 2.42477 5.009V4.5C2.42477 3.30653 2.89888 2.16193 3.74279 1.31802C4.5867 0.474106 5.7313 0 6.92477 0C8.11825 0 9.26284 0.474106 10.1068 1.31802C10.9507 2.16193 11.4248 3.30653 11.4248 4.5V5.01C11.4248 6.026 11.9598 6.968 12.8328 7.489L12.8978 7.527C14.4998 8.484 13.9738 10.93 12.1198 11.143L9.57677 11.435V11.8C9.57677 12.5161 9.29231 13.2028 8.78596 13.7092C8.27961 14.2155 7.59286 14.5 6.87677 14.5C6.16069 14.5 5.47393 14.2155 4.96758 13.7092C4.46124 13.2028 4.17677 12.5161 4.17677 11.8V11.424ZM8.07677 11.5H5.67677V11.8C5.67677 12.1183 5.8032 12.4235 6.02824 12.6485C6.25329 12.8736 6.55851 13 6.87677 13C7.19503 13 7.50026 12.8736 7.7253 12.6485C7.95034 12.4235 8.07677 12.1183 8.07677 11.8V11.5ZM4.92477 10H8.92477L11.9488 9.652C12.0426 9.64116 12.1307 9.60118 12.2007 9.53769C12.2706 9.4742 12.3189 9.39038 12.3388 9.29802C12.3586 9.20566 12.3491 9.1094 12.3114 9.02276C12.2737 8.93612 12.2099 8.86347 12.1288 8.815L12.0638 8.777C11.8239 8.63377 11.5982 8.46802 11.3898 8.282L11.3168 8.215C10.8782 7.80416 10.5284 7.30782 10.289 6.7566C10.0496 6.20538 9.92562 5.61096 9.92477 5.01V4.5C9.92477 4.10603 9.84717 3.71593 9.69641 3.35195C9.54565 2.98797 9.32467 2.65726 9.04609 2.37868C8.76752 2.1001 8.4368 1.87913 8.07282 1.72836C7.70884 1.5776 7.31874 1.5 6.92477 1.5C6.53081 1.5 6.1407 1.5776 5.77672 1.72836C5.41274 1.87913 5.08203 2.1001 4.80345 2.37868C4.52488 2.65726 4.3039 2.98797 4.15313 3.35195C4.00237 3.71593 3.92477 4.10603 3.92477 4.5V5.01C3.92405 5.61089 3.80026 6.20527 3.56102 6.75648C3.32179 7.3077 2.97219 7.80407 2.53377 8.215L2.46077 8.282C2.2516 8.46712 2.026 8.63281 1.78677 8.777L1.72177 8.815C1.64085 8.86368 1.57716 8.93644 1.53962 9.0231C1.50208 9.10975 1.49256 9.20597 1.51239 9.29831C1.53223 9.39064 1.58042 9.47446 1.65023 9.53806C1.72005 9.60166 1.80799 9.64184 1.90177 9.653L4.92477 10Z';
	/* Same structure/classes as snippets/notify-stock-modal.liquid — keep the
	   two in sync. Built only when a guest card click finds no PDP modal. */
	var MODAL_SHELL =
		'<notify-stock-modal class="notify-stock-modal" data-notify-stock-modal>' +
		'<div class="notify-stock-modal__content" role="dialog" aria-modal="true" aria-label="Anunta-ma cand revine in stoc">' +
		'<button id="ModalClose-notify-stock" type="button" class="notify-stock-modal__close" aria-label="Inchide">' +
		'<svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M1 1l8 8M9 1L1 9"/></svg>' +
		'</button>' +
		'<h2 class="notify-stock-modal__title">Anunta-ma cand revine in stoc</h2>' +
		'<div class="notify-stock-modal__product">' +
		'<div class="notify-stock-modal__image"><img data-notify-image alt="" width="163" height="201" hidden></div>' +
		'<div class="notify-stock-modal__info">' +
		'<div class="notify-stock-modal__details">' +
		'<p class="notify-stock-modal__name" data-notify-name></p>' +
		'<p class="notify-stock-modal__meta" data-notify-size hidden></p>' +
		'</div>' +
		'<div>' +
		'<div class="notify-stock-modal__price-row">' +
		'<span class="notify-stock-modal__price" data-notify-price></span>' +
		'<span class="notify-stock-modal__price-compare" data-notify-price-compare hidden></span>' +
		'</div>' +
		'<p class="notify-stock-modal__price-meta">' +
		'<span data-notify-price-net></span>' +
		'<span class="notify-stock-modal__price-dot" data-notify-brand-dot aria-hidden="true" hidden></span>' +
		'<span data-notify-brand hidden></span>' +
		'</p>' +
		'</div>' +
		'</div>' +
		'</div>' +
		'<form class="notify-stock-modal__form" novalidate>' +
		'<div class="notify-stock-modal__sizes" data-notify-sizes-wrap hidden>' +
		'<p class="notify-stock-modal__label">Alege marimile (maxim 3) <span aria-hidden="true">*</span></p>' +
		'<div class="notify-stock-modal__size-list" data-notify-sizes></div>' +
		'</div>' +
		'<label class="notify-stock-modal__label" for="NotifyStockEmail">Email pentru notificare <span aria-hidden="true">*</span></label>' +
		'<input id="NotifyStockEmail" class="notify-stock-modal__input" type="email" name="email" required placeholder="adresa@mail.com" autocomplete="email">' +
		'<p class="notify-stock-modal__error" data-notify-error hidden></p>' +
		'<button type="submit" class="notify-stock-modal__submit">Trimite</button>' +
		'</form>' +
		'</div>' +
		'<script type="application/json" data-notify-data>{"variants":{},"product":null}</' +
		'script>' +
		'</notify-stock-modal>';
	var pending = new Set();

	/* Variant-scoped triggers subscribe one size; the card CTA has no
	   data-variant-id — its UI state is product-scoped (the actual sizes are
	   picked in the modal). */
	function keyFor(productId, variantId) {
		return variantId ? 'v' + variantId : 'p' + productId;
	}

	/* Theme setting first (data-klaviyo-key on the trigger); falls back to the
	   company_id in the Klaviyo app embed's script tag so an unset setting
	   still works while the embed is enabled. */
	var sniffedKey = null;
	function companyIdFor(button) {
		if (button && button.dataset.klaviyoKey) return button.dataset.klaviyoKey;
		if (sniffedKey) return sniffedKey;
		var scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js"]');
		for (var i = 0; i < scripts.length; i++) {
			var match = scripts[i].src.match(/[?&]company_id=([^&]+)/);
			if (match) {
				sniffedKey = match[1];
				return sniffedKey;
			}
		}
		return null;
	}

	function buttonsFor(productId, variantId) {
		if (variantId) {
			return document.querySelectorAll('.notify-stock[data-variant-id="' + variantId + '"]');
		}
		return document.querySelectorAll('.notify-stock[data-product-id="' + productId + '"]:not([data-variant-id])');
	}

	/* Both hosts can show the same size (picker + table on footwear/fashion),
	   and a product can appear on several cards — state applies everywhere.
	   Native disabled until the next page load: the click handler's
	   .is-subscribed bail-out and the CSS pointer-events:none are belt and
	   braces on top, and the card CTA picks up .pcard__cta[disabled]'s dimmed
	   styling for free. */
	function markSubscribed(productId, variantId) {
		buttonsFor(productId, variantId).forEach(function (button) {
			button.classList.add('is-subscribed');
			button.disabled = true;
		});
	}

	function setBusy(productId, variantId, busy) {
		buttonsFor(productId, variantId).forEach(function (button) {
			button.classList.toggle('is-busy', busy);
			if (busy) {
				button.setAttribute('aria-busy', 'true');
			} else {
				button.removeAttribute('aria-busy');
			}
		});
	}

	function flashError(button) {
		button.classList.add('is-error');
		setTimeout(function () {
			button.classList.remove('is-error');
		}, 2000);
	}

	/* Confirmation toast — deliberate visual copy of the wishlist toast
	   (card-wishlist.js / component-wishlist-toast.css); change them together.
	   The red .notify-toast--error variant is notify-only: it carries the
	   generic message, while the actual Klaviyo error detail goes to
	   console.error (see subscribe()). One element is reused for both states,
	   repainted on every show. */
	var toastTimer = null;

	var TOAST_BELL_ICON =
		'<svg class="notify-toast__icon" viewBox="0 0 13.85 14.5" fill="currentColor" aria-hidden="true">' +
		'<path fill-rule="evenodd" clip-rule="evenodd" d="' +
		BELL_PATH +
		'"/></svg>';
	var TOAST_ERROR_ICON =
		'<svg class="notify-toast__icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
		'<circle cx="8" cy="8" r="6.75"/>' +
		'<path d="M8 4.75v3.75" stroke-linecap="round"/>' +
		'<circle cx="8" cy="11.4" r="0.9" fill="currentColor" stroke="none"/></svg>';

	function showToast(message, isError) {
		var toast = document.querySelector('.notify-toast');
		if (!toast) {
			toast = document.createElement('div');
			toast.className = 'notify-toast';
			toast.setAttribute('role', 'status');
			document.body.appendChild(toast);
		}
		toast.classList.toggle('notify-toast--error', !!isError);
		toast.innerHTML = (isError ? TOAST_ERROR_ICON : TOAST_BELL_ICON) + '<span></span>';
		toast.lastElementChild.textContent = message;
		/* Restart the slide-in even when a show is already in progress. */
		toast.classList.remove('is-visible');
		void toast.offsetWidth;
		toast.classList.add('is-visible');
		clearTimeout(toastTimer);
		toastTimer = setTimeout(function () {
			toast.classList.remove('is-visible');
		}, 3000);
	}

	/* One Klaviyo request per variant (their BIS API is variant-scoped). All
	   requests settle; the promise resolves when AT LEAST ONE is accepted
	   (HTTP 202, empty body) — a partial failure still means the shopper WILL
	   be notified for the sizes that went through, so callers show success.
	   It rejects only when every request fails. Klaviyo error details are
	   English JSON:API — callers surface the generic Romanian message, the
	   per-variant detail is console.error'd here (status + body for API
	   rejections, the thrown error for network failures) and a partial
	   outcome is console.warn'd. */
	function subscribe(variantIds, email, companyId) {
		if (!companyId || !email || !variantIds.length) {
			console.error('[notify-stock] Subscription skipped — missing data:', {
				companyId: companyId || '(missing — set the klaviyo_public_api_key theme setting)',
				email: email ? '(set)' : '(missing)',
				variantIds: variantIds
			});
			return Promise.reject(new Error(''));
		}

		/* Resolves true/false (accepted or not) — never rejects, so one bad
		   variant can't sink the whole batch. */
		function postOne(variantId) {
			return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', revision: REVISION },
				body: JSON.stringify({
					data: {
						type: 'back-in-stock-subscription',
						attributes: {
							profile: { data: { type: 'profile', attributes: { email: email } } },
							channels: ['EMAIL']
						},
						relationships: {
							variant: {
								data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
							}
						}
					}
				})
			})
				.catch(function (networkError) {
					console.error('[notify-stock] Klaviyo request failed for variant ' + variantId + ':', networkError);
					return null;
				})
				.then(function (response) {
					if (!response) return false;
					if (response.ok) return true;
					return response
						.text()
						.catch(function () {
							return '(unreadable body)';
						})
						.then(function (body) {
							console.error(
								'[notify-stock] Klaviyo rejected variant ' +
									variantId +
									' (HTTP ' +
									response.status +
									'):',
								body
							);
							return false;
						});
				});
		}

		/* SEQUENTIAL AND SPACED on purpose — do not parallelize or tighten.
		   The 202 only means "queued": Klaviyo ingests these asynchronously,
		   and same-profile subscriptions that arrive too close together get
		   dropped server-side (observed: 7 sequential ~300ms-apart requests,
		   all 202 with 7 distinct variant ids → only 1 registered). Spacing
		   alone isn't enough either — even 2s spacing sometimes topped out at
		   3 accepted, hence the MAX_SIZES cap on the batch; the modal's busy
		   state covers the wait (~1s per extra size). */
		function wait(ms) {
			return new Promise(function (resolve) {
				setTimeout(resolve, ms);
			});
		}

		var results = [];
		var chain = Promise.resolve();
		variantIds.forEach(function (variantId, index) {
			chain = chain
				.then(function () {
					return index === 0 ? null : wait(REQUEST_SPACING_MS);
				})
				.then(function () {
					return postOne(variantId);
				})
				.then(function (accepted) {
					results.push(accepted);
				});
		});

		return chain.then(function () {
			var accepted = results.filter(Boolean).length;
			if (!accepted) throw new Error('');
			if (accepted < results.length) {
				console.warn(
					'[notify-stock] Partial success: ' +
						accepted +
						'/' +
						results.length +
						' subscriptions accepted — treating as success (failures logged above).'
				);
			}
		});
	}

	/* The PDP renders the modal server-side; on card-only pages the first guest
	   click builds the same shell. Returns null when ModalDialog is missing
	   (the trigger then degrades to a no-op — no crash). */
	function ensureModal() {
		var modal = document.querySelector('[data-notify-stock-modal]');
		if (modal) return modal;
		if (typeof ModalDialog === 'undefined') return null;
		var host = document.createElement('div');
		host.innerHTML = MODAL_SHELL;
		modal = host.firstElementChild;
		document.body.appendChild(modal);
		return modal;
	}

	document.addEventListener('click', function (event) {
		var button = event.target.closest('.notify-stock');
		if (!button || button.classList.contains('is-subscribed')) return;

		var productId = button.dataset.productId;
		var variantId = button.dataset.variantId || null;
		if (!productId || pending.has(keyFor(productId, variantId))) return;

		/* Only variant-scoped bells with a logged-in email (data-customer-email,
		   emitted for logged-in customers only) subscribe directly — the size is
		   already chosen. Card CTAs ALWAYS open the modal, logged-in included:
		   Klaviyo subscriptions are per-variant, so the shopper has to pick which
		   sizes to watch (the modal prefills their email). */
		if (variantId && button.dataset.customerEmail) {
			var key = keyFor(productId, variantId);
			pending.add(key);
			setBusy(productId, variantId, true);
			subscribe([variantId], button.dataset.customerEmail, companyIdFor(button))
				.then(function () {
					markSubscribed(productId, variantId);
					showToast(TOAST_TEXT);
				})
				.catch(function () {
					flashError(button);
					showToast(GENERIC_ERROR, true);
				})
				.finally(function () {
					setBusy(productId, variantId, false);
					pending.delete(key);
				});
		} else {
			var modal = ensureModal();
			if (modal && typeof modal.openForVariant === 'function') {
				modal.openForVariant(button);
			}
		}
	});

	/* Subscription modal — guests (email collection) AND logged-in card-CTA
	   clicks (size selection, email prefilled). ModalDialog (global.js)
	   provides show/hide, ESC, focus trap, backdrop click (event.target ===
	   the element) and the ModalClose button. If it isn't available the
	   trigger simply does nothing. */
	if (typeof ModalDialog === 'undefined' || customElements.get('notify-stock-modal')) return;

	customElements.define(
		'notify-stock-modal',
		class NotifyStockModal extends ModalDialog {
			constructor() {
				super();
				this.productId = null;
				this.variantId = null;
				this.companyId = null;
				this.selectedSizes = new Set();
				this.form = this.querySelector('.notify-stock-modal__form');
				this.emailInput = this.querySelector('.notify-stock-modal__input');
				this.errorElement = this.querySelector('[data-notify-error]');
				this.submitButton = this.querySelector('.notify-stock-modal__submit');
				this.sizesWrap = this.querySelector('[data-notify-sizes-wrap]');
				this.sizeList = this.querySelector('[data-notify-sizes]');

				try {
					this.data = JSON.parse(this.querySelector('[data-notify-data]').textContent);
				} catch (error) {
					this.data = {};
				}
				if (!this.data || typeof this.data !== 'object') this.data = {};
				if (!this.data.variants) this.data.variants = {};

				this.form.addEventListener('submit', this.onSubmit.bind(this));

				/* Size chips are (re)built per open — one delegated listener
				   outlives them all. */
				if (this.sizeList) {
					var self = this;
					this.sizeList.addEventListener('click', function (event) {
						var chip = event.target.closest('.notify-stock-modal__size');
						if (!chip) return;
						var id = chip.dataset.variantId;
						var selected = !self.selectedSizes.delete(id);
						if (selected && self.selectedSizes.size >= MAX_SIZES) {
							self.showError('Poti selecta maxim ' + MAX_SIZES + ' marimi.');
							return;
						}
						if (selected) self.selectedSizes.add(id);
						chip.classList.toggle('is-selected', selected);
						chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
						self.hideError();
					});
				}
			}

			/* Repaints every field on each open: variant bells read the PDP blob
			   (product info + per-variant prices), card CTAs carry their own
			   data-notify-* payload — so one shell serves any product. */
			openForVariant(button) {
				this.productId = button.dataset.productId;
				this.variantId = button.dataset.variantId || null;
				this.companyId = companyIdFor(button);

				/* Card CTAs carry the product's sizes in data-notify-variants
				   (Klaviyo subscriptions are per-variant, so the shopper picks
				   which sizes to watch); variant bells already know their size
				   and render no selector. A single size is preselected. */
				this.selectedSizes.clear();
				var sizeOptions = [];
				if (!this.variantId && button.dataset.notifyVariants) {
					try {
						sizeOptions = JSON.parse(button.dataset.notifyVariants);
					} catch (error) {
						sizeOptions = [];
					}
				}
				if (this.sizeList) {
					this.sizeList.innerHTML = '';
					for (var i = 0; i < sizeOptions.length; i++) {
						var chip = document.createElement('button');
						chip.type = 'button';
						chip.className = 'notify-stock-modal__size';
						chip.dataset.variantId = String(sizeOptions[i].id);
						chip.textContent = sizeOptions[i].title;
						var preselected = sizeOptions.length === 1;
						if (preselected) {
							this.selectedSizes.add(chip.dataset.variantId);
							chip.classList.add('is-selected');
						}
						chip.setAttribute('aria-pressed', preselected ? 'true' : 'false');
						this.sizeList.appendChild(chip);
					}
				}
				if (this.sizesWrap) this.sizesWrap.hidden = sizeOptions.length === 0;

				var details;
				var blobVariant = this.variantId && this.data.variants[this.variantId];
				if (blobVariant) {
					var blobProduct = this.data.product || {};
					details = {
						title: blobProduct.title,
						image: blobProduct.image,
						brand: blobProduct.brand,
						size: button.dataset.variantTitle || blobVariant.title,
						price: blobVariant.price,
						compare: blobVariant.compare,
						net: blobVariant.net
					};
				} else {
					details = {
						title: button.dataset.notifyTitle,
						image: button.dataset.notifyImage,
						brand: button.dataset.notifyBrand,
						size: button.dataset.variantTitle,
						price: button.dataset.notifyPrice,
						compare: button.dataset.notifyCompare,
						net: button.dataset.notifyNet
					};
				}

				this.setField('[data-notify-name]', details.title);
				this.setField('[data-notify-size]', details.size);
				this.setField('[data-notify-price]', details.price);
				this.setField('[data-notify-price-compare]', details.compare);
				this.setField('[data-notify-price-net]', details.net);
				this.setField('[data-notify-brand]', details.brand);

				var dot = this.querySelector('[data-notify-brand-dot]');
				if (dot) dot.hidden = !details.brand;

				var image = this.querySelector('[data-notify-image]');
				if (image) {
					if (details.image) image.setAttribute('src', details.image);
					image.hidden = !details.image && !image.getAttribute('src');
				}

				/* Logged-in shoppers land here via card CTAs — spare them retyping
				   the email they're logged in with (still editable). */
				this.emailInput.value = button.dataset.customerEmail || '';
				this.hideError();
				this.show(button);
			}

			setField(selector, value) {
				var element = this.querySelector(selector);
				if (!element) return;
				element.textContent = value || '';
				element.hidden = !value;
			}

			onSubmit(event) {
				event.preventDefault();
				if (!this.emailInput.value || !this.emailInput.checkValidity()) {
					this.showError('Introdu o adresa de email valida.');
					return;
				}

				var variantIds = this.variantId ? [this.variantId] : Array.from(this.selectedSizes);
				if (!variantIds.length) {
					this.showError('Selecteaza cel putin o marime.');
					return;
				}

				var self = this;
				this.hideError();
				this.submitButton.setAttribute('aria-busy', 'true');
				subscribe(variantIds, this.emailInput.value, this.companyId)
					.then(function () {
						self.hide();
						markSubscribed(self.productId, self.variantId);
						showToast(TOAST_TEXT);
					})
					.catch(function (error) {
						self.showError(error.message || GENERIC_ERROR);
					})
					.finally(function () {
						self.submitButton.removeAttribute('aria-busy');
					});
			}

			showError(message) {
				this.errorElement.textContent = message;
				this.errorElement.hidden = false;
			}

			hideError() {
				this.errorElement.textContent = '';
				this.errorElement.hidden = true;
			}
		}
	);
})();
